import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { ClockIcon } from '@heroicons/react/24/outline'
import { GET_PLANNED_ORDERS_TODAY } from '../../graphql/queries'
import { usePermissions } from '../../hooks/usePermissions'
import { PERMISSIONS } from '../../lib/permissions'
import { formatEuros, formatTime, localDayBoundsUtc } from '../../lib/format'
import type { Order } from '../../types/order'

// "Planned orders today": dormant SCHEDULED webshop pre-orders due today, so
// front-of-house sees what's coming before the API cron releases it to the
// kitchen (~40 min ahead). View-only by owner decision — manual release is
// rejected server-side (pickup times are immutable; cancel + re-place is the
// only way to change one). No pollInterval, matching the other dashboard
// widgets: a just-released order may linger in the list until the next
// cache-and-network fetch (dashboard revisit), which is acceptable.

const PlannedOrdersWidget = () => {
  const navigate = useNavigate()
  const { hasAnyPermission } = usePermissions()

  // Real order.list.* tiers only. NOT canViewOrders(): that also passes on
  // order.details.*, which the API's list scope rejects (recentOrders would
  // error). The vestigial *.planned constants are never seeded — don't gate
  // on them either.
  const canList = hasAnyPermission([
    PERMISSIONS.ORDER_LIST_TODAY,
    PERMISSIONS.ORDER_LIST_7DAYS,
    PERMISSIONS.ORDER_LIST_ALL,
  ])

  // Local day boundaries, sent as ISO UTC. Computed per render, NOT memoized:
  // a POS terminal can sit mounted across midnight, and a mount-frozen window
  // would keep querying yesterday. The strings are stable within a day and
  // Apollo deep-compares variables, so this causes no extra refetches; the
  // first re-render after midnight re-scopes the query to the new day.
  const { scheduledFrom, scheduledTo } = localDayBoundsUtc()

  const { data, loading, error } = useQuery(GET_PLANNED_ORDERS_TODAY, {
    variables: {
      filters: {
        statuses: ['SCHEDULED'],
        scheduled_from: scheduledFrom,
        scheduled_to: scheduledTo,
      },
      sortField: 'SCHEDULED_FOR',
      sortDirection: 'ASC',
    },
    fetchPolicy: 'cache-and-network',
    skip: !canList,
  })

  const orders: Order[] = data?.recentOrders ?? []

  // Nothing to say → no card (AnnouncementsWidget precedent: don't add noise).
  if (!canList || error || (loading && !data) || orders.length === 0) return null

  return (
    <div className="rounded-2xl border border-gray-100 shadow-sm bg-white p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
          Geplande bestellingen vandaag
        </h2>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-cyan-700 bg-cyan-100 rounded-full px-2 py-0.5">
          <ClockIcon className="h-3.5 w-3.5" />
          {orders.length}
        </span>
      </div>

      <div className="space-y-2">
        {orders.map((order) => (
          <button
            key={order.id}
            onClick={() => navigate(`/orders/${order.id}`)}
            className="w-full flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-cyan-50 px-3 py-2.5 text-left transition-colors"
          >
            <span className="text-sm font-semibold text-cyan-800 tabular-nums flex-shrink-0 w-12">
              {order.scheduled_for ? formatTime(order.scheduled_for) : '—'}
            </span>
            <span className="text-sm text-gray-900 font-medium truncate">
              {order.order_number}
            </span>
            <span className="text-sm text-gray-500 truncate flex-1">
              {order.customer_name || '—'}
            </span>
            <span className="text-sm font-medium text-gray-900 flex-shrink-0">
              {formatEuros(order.total_cents)}
            </span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default PlannedOrdersWidget
